import Cookies from "js-cookie";
import { Fragment, useEffect } from "react";

import type { UserProfile } from "@/types/user";

import axios from "@/api/axios";
import { usePrivateAxios } from "@/api/use-private-axios";
import { AppSidebar } from "@/components/dashboard/app-sidebar";
import { NotificationPopover } from "@/components/dashboard/notification-popover";
import { ProductRequestDialog } from "@/components/product-request/product-request-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
	SidebarInset,
	SidebarProvider,
	SidebarTrigger
} from "@/components/ui/sidebar";
import { VENDOR_SIDEBAR_DATA } from "@/lib/sidebar-constants";
import { formatKebabCase } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	Outlet,
	redirect,
	useLocation
} from "@tanstack/react-router";

export async function refreshToken() {
	const { user, tokens } = useAuthStore.getState();

	if (!user) {
		throw new Error("User cannot be null");
	}

	if (!tokens?.refreshToken) {
		throw new Error("Refresh token is not available");
	}

	try {
		const response = await axios.post(
			"auth/refresh_token",
			{
				userId: user.id,
				oldRefreshToken: tokens.refreshToken
			},
			{
				headers: {
					"Content-Type": "application/json"
				}
			}
		);

		const { access_token, refreshToken: refresh_token } = response.data.data;

		return { access_token, refresh_token };
	}
	catch (error) {
		// @ts-expect-error error type is unknown
		if (error.response?.data?.message === "Token is already invalid") {
			throw new Error("Token is already invalid");
		}
		console.error("Error refreshing token:", error);
		throw error;
	}
}

export const Route = createFileRoute("/vendor")({
	loader: async () => {
		const { setTokens, tokens } = useAuthStore.getState();
		try {
			await axios.get<{ data: UserProfile }>("/auth/user_profile", {
				headers: tokens?.accessToken
					? { Authorization: `Bearer ${tokens.accessToken}` }
					: undefined
			});
		}
		catch (error: unknown) {
			// @ts-expect-error error type is unknown
			if (error.response?.status >= 400) {
				try {
					const { access_token, refresh_token } = await refreshToken();

					if (access_token && refresh_token) {
						setTokens({
							accessToken: access_token,
							refreshToken: refresh_token
						});
					}
				}
				catch {
					// Keep the existing session. A refresh failure must not
					// surface as "switch user type failed" after a 201.
				}
			}
		}
		const { user } = useAuthStore.getState();
		if (!user || user.status !== "ACTIVE") {
			throw redirect({
				to: "/sign-in"
			});
		}
		if (String(user.userType || "").toUpperCase() !== "VENDOR") {
			throw redirect({
				to: "/buyer/home"
			});
		}
		if (location.pathname === "/vendor" || location.pathname === "/vendor/") {
			throw redirect({
				to: "/vendor/home"
			});
		}
	},
	component: RouteComponent
});

function RouteComponent() {
	let breadcrumbRoute: string = "";

	const location = useLocation();
	const user = useAuthStore(state => state.user);
	const userInitials = `${user?.firstName.charAt(0)}${user?.lastName.charAt(
		0
	)}`;

	const privateAxios = usePrivateAxios();
	const accessToken = useAuthStore(state => state.tokens?.accessToken);
	const queryClient = useQueryClient();
	useEffect(() => {
		if (!accessToken)
			return;
		queryClient.prefetchQuery({
			queryKey: ["order-disputes"],
			queryFn: async () => {
				return await privateAxios.get("/order/list_desputes-for-orders", {
					headers: { Authorization: `Bearer ${accessToken}` }
				});
			}
		});
	}, [accessToken, privateAxios, queryClient]);

	return (
		<section className="w-full max-w-[100vw] overflow-x-hidden">
			<SidebarProvider className="min-h-svh min-w-0" defaultOpen={Cookies.get("sidebar:state") === "true"}>
				<AppSidebar data={VENDOR_SIDEBAR_DATA} />
				<SidebarInset className="min-w-0 overflow-x-hidden bg-gray-100">
					<header className="flex h-14 shrink-0 items-center gap-2 sm:h-16">
						<div className="flex min-w-0 flex-1 items-center gap-2 px-3 sm:px-4">
							<SidebarTrigger className="-ml-1 shrink-0" />
							<Separator orientation="vertical" className="mr-2 hidden h-4 shrink-0 sm:block" />
							<Breadcrumb className="min-w-0">
								<BreadcrumbList>
									{location.pathname
										.split("/")
										.filter(path => !!path)
										.map((path, idx) => {
											const len = location.pathname.length;
											breadcrumbRoute = breadcrumbRoute.concat(`/${path}`);
											const isActive
												= location.pathname.endsWith(breadcrumbRoute, len)
													|| location.pathname.endsWith(breadcrumbRoute, len - 1);
											const formattedpath = formatKebabCase(path);
											return (
												idx > 0 && (
													<Fragment key={breadcrumbRoute}>
														{idx > 1 && (
															<BreadcrumbSeparator className="hidden md:block" />
														)}
														{isActive
															? (
																	<BreadcrumbItem className="min-w-0">
																		<BreadcrumbPage className="block max-w-[8rem] truncate capitalize sm:max-w-[16rem]">
																			{formattedpath}
																		</BreadcrumbPage>
																	</BreadcrumbItem>
																)
															: (
																	<BreadcrumbItem className="hidden md:block">
																		<BreadcrumbLink asChild>
																			<Link
																				to={breadcrumbRoute}
																				className="capitalize"
																			>
																				{formattedpath}
																			</Link>
																		</BreadcrumbLink>
																	</BreadcrumbItem>
																)}
													</Fragment>
												)
											);
										})}
								</BreadcrumbList>
							</Breadcrumb>
						</div>
						<div className="flex shrink-0 items-center gap-1 pr-2 sm:gap-2 sm:pr-3">
							<div className="hidden md:block">
								<ProductRequestDialog />
							</div>
							<NotificationPopover />
							<Avatar className="mx-1 size-8 rounded-full border-2 border-primary sm:size-10">
								<AvatarImage src="/change" alt={user?.fullName} />
								<AvatarFallback className="rounded-lg text-xs sm:text-sm">
									{userInitials}
								</AvatarFallback>
							</Avatar>
						</div>
					</header>
					<div className="flex min-w-0 flex-1 flex-col gap-4 p-3 pt-0 sm:p-4 sm:pt-0">
						<Outlet />
					</div>
				</SidebarInset>
			</SidebarProvider>
		</section>
	);
}
